import { getCustomMenuSetsAPI, getCustomMenuSetDetailAPI } from "@modules/apis/custom-menu-sets";
import { useSelector } from "react-redux";
import { useLocation } from "react-router";
import { useCallback } from "react";
import menuService from "@services/menuService";
import AssetList from "@routes/asset/List";
import { CUSTOM_MENU_DOMAIN, CUSTOM_MENU_TYPE, MENU_LIST } from "@constants";
import { siteMode } from "@cores/siteMode";

const useCustomMenus = () => {
    const project = useSelector(({ project }) => project);
    const space = useSelector(({ stage }) => stage);
    const customMenus = useSelector(({ customMenus }) => customMenus);
    const { pathname } = useLocation();
    const { spaceId, projectId } = menuService.parse(pathname);

    const isLibrary = siteMode.get() === "CMS";

    const loadCustomMenus = async () => {
        try {
            const getComponentByDomain = (domain, type) => {
                switch (domain) {
                    case CUSTOM_MENU_DOMAIN.ASSETS:
                        if (type === CUSTOM_MENU_TYPE.LIST) return AssetList;
                        else return null;
                    default:
                        return null;
                }
            };
            const getFilteredMenus = (menus) => {
                if (isLibrary) return menus.filter((el) => el.libraryVisible);
                else return menus.filter((el) => el.consoleVisible);
            };

            const getConnectedMenuId = (domain) => {
                switch (domain) {
                    case CUSTOM_MENU_DOMAIN.ASSETS:
                        return MENU_LIST.ASSETS;
                    default:
                        return null;
                }
            };

            let setId = pathname.split("/customMenuSets/")?.[1]?.split("/")?.[0];
            if (!setId) {
                const response = await getCustomMenuSetsAPI(space.endpoint, project.id, {});
                setId = response.data?.results?.[0]?.id;
            }
            const response = await getCustomMenuSetDetailAPI(space.endpoint, project.id, setId);
            return getFilteredMenus(response.data?.menus)?.map((m) => {
                const { id, name, iconType, children, landingInfo } = m;
                return {
                    id,
                    name,
                    iconType,
                    isOpen: true,
                    subMenus: getFilteredMenus(children).map((c) => {
                        const { domain, type, attributes } = c.landingInfo;
                        return {
                            id: c.id,
                            name: c.name,
                            iconType: c.iconType,
                            searchParams: attributes,
                            component: getComponentByDomain(domain, type),
                            connectedMenuId: getConnectedMenuId(domain),
                        };
                    }),
                    ...(landingInfo
                        ? {
                              searchParams: landingInfo?.attributes,
                              component: getComponentByDomain(landingInfo?.domain, landingInfo?.type),
                              connectedMenuId: getConnectedMenuId(landingInfo?.domain),
                          }
                        : {}),
                };
            });
        } catch (e) {}
    };

    // Custom menu breadcrumb logic
    const findObjectById = useCallback((menuArray, targetId) => {
        const menuMap = new Map();
        const relationMap = new Map();

        for (const menu of menuArray) {
            menuMap.set(menu.id, menu);

            if (menu.subMenus && menu.subMenus.length) {
                for (const subMenu of menu.subMenus) {
                    menuMap.set(subMenu.id, subMenu);
                    relationMap.set(subMenu.id, menu);
                }
            }
        }

        if (menuMap.has(targetId)) {
            const found = menuMap.get(targetId);
            const parent = relationMap.get(targetId);

            return parent ? [parent, found] : [found];
        }

        return [];
    }, []);

    const parseDetailTypeMenu = useCallback((menu) => {
        if (menu.type !== CUSTOM_MENU_TYPE.DETAIL) return "";

        if (menu.domain === CUSTOM_MENU_DOMAIN.COLLECTIONS) {
            return `/${menu.searchParams.collections?.[0]?.id}`;
        }
        return "";
    }, []);

    const getCustomMenuBreadcrumb = useCallback(
        (isFromListMenu) => {
            if (!customMenus?.data) return [];

            const menuId = pathname.split("/customMenuSets/")?.[1]?.split("/")?.[1];
            if (!menuId) return [];

            const result = findObjectById(customMenus.data, menuId);
            return result.map((item, index) => ({
                id: item.id,
                name: item.name,
                path:
                    (index < result.length - 1 || isFromListMenu) && !!item.domain
                        ? `${isLibrary ? "/library" : ""}/spaces/${spaceId}/projects/${projectId}/customMenuSets/${
                              customMenus.setId
                          }/${item.id}${parseDetailTypeMenu(item)}`
                        : null,
            }));
        },
        [customMenus, pathname, findObjectById, isLibrary, spaceId, projectId, parseDetailTypeMenu],
    );

    const hasMenuWithDomain = (targetDomain) => {
        return customMenus?.data?.find((m) => {
            return m.domain === targetDomain || m.subMenus?.some((c) => c?.domain === targetDomain);
        });
    };

    return {
        loadCustomMenus,
        getCustomMenuBreadcrumb,
        hasMenuWithDomain,
    };
};

export default useCustomMenus;
